import { Command } from 'commander'
import pc from 'picocolors'
import {
  existsSync,
  mkdirSync,
  writeFileSync,
  readFileSync,
  readdirSync,
  rmSync,
  statSync,
  cpSync,
} from 'fs'
import { join, dirname, relative } from 'path'
import { fileURLToPath } from 'url'
import { loadConfig } from '../../config'

const __dirname = dirname(fileURLToPath(import.meta.url))

export const buildCommand = new Command('build')
  .description('Build the presentation for deployment')
  .option('-o, --outDir <dir>', 'Output directory', 'dist')
  .option('-c, --config <path>', 'Path to config file')
  .action(async (options: { outDir: string; config?: string }) => {
    const cwd = process.cwd()

    console.log(pc.cyan('\nBrixon Build'))
    console.log(pc.dim('─'.repeat(40)))

    // Load config
    const config = await loadConfig(cwd)
    if (!config) {
      console.error(pc.red('\nError: No brixon.config.ts found.'))
      console.error(pc.dim('Run `brixon init` to create a new project.'))
      process.exit(1)
    }

    const outDir = join(cwd, options.outDir || config.build?.outDir || 'dist')

    // Check for presentation file
    const presentationPaths = [
      join(cwd, 'src', 'presentation.tsx'),
      join(cwd, 'src', 'presentation.jsx'),
      join(cwd, 'presentation.tsx'),
    ]

    let presentationPath: string | null = null
    for (const p of presentationPaths) {
      if (existsSync(p)) {
        presentationPath = p
        break
      }
    }

    if (!presentationPath) {
      console.error(pc.red('\nError: No presentation file found.'))
      console.error(pc.dim('Expected: src/presentation.tsx'))
      process.exit(1)
    }

    console.log(pc.white(`Building: ${pc.bold(config.presentation.title)}`))
    console.log(pc.dim(`Slug: ${config.presentation.slug}`))

    // Create temp build directory
    const tempDir = join(cwd, '.brixon-build')

    try {
      // Clean up any previous temp dir
      if (existsSync(tempDir)) {
        rmSync(tempDir, { recursive: true })
      }
      mkdirSync(tempDir, { recursive: true })

      // Create src directory in temp
      mkdirSync(join(tempDir, 'src'), { recursive: true })

      // 1. Copy index.html template and replace {{TITLE}}
      const templatePath = resolveTemplatePath()
      const htmlTemplate = readFileSync(templatePath, 'utf-8')
      const html = htmlTemplate.replace(/\{\{TITLE\}\}/g, config.presentation.title)
      writeFileSync(join(tempDir, 'index.html'), html)

      // 2. Copy the presentation file to temp/src
      const presentationContent = readFileSync(presentationPath, 'utf-8')
      writeFileSync(join(tempDir, 'src', 'presentation.tsx'), presentationContent)

      // 3. Create main.tsx entry point
      const mainTsx = `import React from 'react'
import { createRoot } from 'react-dom/client'
import Presentation from './presentation'

const container = document.getElementById('root')
if (container) {
  const root = createRoot(container)
  root.render(<Presentation />)
}
`
      writeFileSync(join(tempDir, 'src', 'main.tsx'), mainTsx)

      // 4. Copy public directory if it exists
      const publicDir = join(cwd, 'public')
      if (existsSync(publicDir)) {
        cpSync(publicDir, join(tempDir, 'public'), { recursive: true })
      }

      // 5. Run Vite build programmatically (inline config, no config file needed)
      console.log(pc.dim('\nBundling with Vite...'))

      // Dynamic imports to avoid issues at module load time
      const vite = await import('vite')
      const reactPlugin = await import('@vitejs/plugin-react')

      await vite.build({
        root: tempDir,
        base: './', // Relative paths for iframe embedding
        configFile: false, // Don't look for config file
        logLevel: 'warn',
        plugins: [reactPlugin.default()],
        build: {
          outDir: 'dist',
          emptyOutDir: true,
          rollupOptions: {
            output: {
              manualChunks: undefined,
            },
          },
        },
        resolve: {
          alias: {
            '@': join(cwd, 'src'),
          },
        },
        // Optimize deps to pre-bundle React
        optimizeDeps: {
          include: ['react', 'react-dom'],
        },
      })

      // 8. Move Vite output to final dist
      const viteOutDir = join(tempDir, 'dist')

      // Clean final output directory
      if (existsSync(outDir)) {
        rmSync(outDir, { recursive: true })
      }
      mkdirSync(outDir, { recursive: true })

      // Copy all files from Vite output
      cpSync(viteOutDir, outDir, { recursive: true })

      // 9. Generate manifest.json with file list
      const files = listFilesRecursive(outDir, outDir)
      const manifest = {
        slug: config.presentation.slug,
        title: config.presentation.title,
        subtitle: config.presentation.subtitle,
        clientName: config.presentation.clientName,
        buildTime: new Date().toISOString(),
        version: '1.0.0',
        files,
      }

      writeFileSync(join(outDir, 'manifest.json'), JSON.stringify(manifest, null, 2))

      // 10. Success output
      console.log(pc.green('\n✓ Build complete!'))
      console.log(pc.dim(`\nOutput: ${outDir}`))
      console.log(pc.dim(`Files: ${files.length}`))
      files.slice(0, 10).forEach((f) => console.log(pc.dim(`  - ${f}`)))
      if (files.length > 10) {
        console.log(pc.dim(`  ... and ${files.length - 10} more`))
      }
    } catch (error) {
      console.error(pc.red('\nError: Build failed.'))
      console.error(pc.dim(String(error)))
      process.exit(1)
    } finally {
      // 11. Clean up temp directory
      if (existsSync(tempDir)) {
        rmSync(tempDir, { recursive: true })
      }
    }
  })

/**
 * Resolve the path to the index.html template
 * Handles both development (src) and production (dist) paths
 */
function resolveTemplatePath(): string {
  // When running from compiled dist/cli/index.js (bundled)
  // __dirname = dist/cli/, so ../.. = package root
  const distPath = join(__dirname, '..', '..', 'templates', 'index.html')
  if (existsSync(distPath)) {
    return distPath
  }

  // Fallback: Look relative to package root (for development)
  const packageRoot = join(__dirname, '..', '..', '..')
  const templatePath = join(packageRoot, 'templates', 'index.html')
  if (existsSync(templatePath)) {
    return templatePath
  }

  // Last resort: node_modules path
  const nodeModulesPath = join(
    process.cwd(),
    'node_modules',
    '@brixon',
    'presentation-sdk',
    'templates',
    'index.html'
  )
  if (existsSync(nodeModulesPath)) {
    return nodeModulesPath
  }

  throw new Error('Could not find index.html template. Ensure @brixon/presentation-sdk is properly installed.')
}

function listFilesRecursive(dir: string, baseDir: string): string[] {
  const files: string[] = []

  const entries = readdirSync(dir)
  for (const entry of entries) {
    const fullPath = join(dir, entry)
    if (statSync(fullPath).isDirectory()) {
      files.push(...listFilesRecursive(fullPath, baseDir))
    } else {
      files.push(relative(baseDir, fullPath))
    }
  }

  return files
}
